import {CardDefinition, DeliveryOptions} from '../../../common/types'
import {ActionFlowConfig} from '../../../common/types/action-flows'
import {addMissingKeys} from './add-missing-keys'

export const sanitiseForUI = (
  config: ActionFlowConfig,
  {segmentIds}: {segmentIds?: string[]}
): ActionFlowConfig => {
  const sanitisedCardTemplates: ActionFlowConfig['cardTemplates'] = {}
  let cardVariables: ActionFlowConfig['variables'] = {}
  const cardTemplateEntries = Object.entries(config.cardTemplates ?? {})
  const cardDelivery: Record<string, DeliveryOptions> = {}

  for (const [id, definition] of cardTemplateEntries) {
    const {
      variables: cardDefinitionVariables,
      metadata: cardDefinitionMetadata,
      ...cardDefinition
    } = definition

    if (cardDefinitionVariables) {
      cardVariables = {
        ...cardVariables,
        ...cardDefinitionVariables
      }
    }

    const {delivery, ...metadata} = cardDefinitionMetadata

    if (delivery) {
      // we've got delivery in the definition, lift it out and store separately
      // eslint-disable-next-line functional/immutable-data
      cardDelivery[id] = delivery
    }

    const prunedDefinition = {
      ...cardDefinition,
      metadata // note metadata now excludes delivery
    }

    // eslint-disable-next-line functional/immutable-data
    sanitisedCardTemplates[id] = addMissingKeys(
      // ensures all elements in the definition have keys set
      prunedDefinition as CardDefinition
    )
  }

  const sanitisedSteps: ActionFlowConfig['steps'] = Object.fromEntries(
    Object.entries(config.steps ?? {}).map(([id, step]) => {
      // set the delivery options on the step if it's a create-card step
      if (
        step.type === 'create-card' &&
        step.properties.cardTemplateId in cardDelivery
      ) {
        return [
          id,
          {
            ...step,
            properties: {
              ...step.properties,
              cardOptions: {
                ...cardDelivery[step.properties.cardTemplateId],
                ...step.properties?.cardOptions
              }
            }
          }
        ]
      }
      return [id, step]
    })
  )

  const sanitisedTriggers: ActionFlowConfig['triggers'] = Object.fromEntries(
    Object.entries(config.triggers ?? {})
      .map(([id, trigger]) => {
        let name = trigger.name
        // Give triggers a name if they don't have one
        if (trigger.type === 'event-listener' && !trigger.name) {
          name = 'Segment trigger'
        }

        // Remove any segment triggers where the segment doesn't exist
        if (
          // Note checking truthiness here. Undefined suggests user unable to see segments, so do nothing.
          // Empty list suggests can see but there are none so any segment triggers would be invalid.
          segmentIds &&
          trigger.type === 'event-listener' &&
          trigger.properties.condition.type === 'segment'
        ) {
          if (segmentIds.includes(trigger.properties.condition.segmentId)) {
            return [id, {...trigger, name}]
          }
          return []
        }

        return [
          id,
          {
            ...trigger,
            name
          }
        ]
      })
      .filter(([id]) => id) // remove empty items
  )

  return {
    ...config,
    variables: {
      ...cardVariables, // bring any card variables to top-level
      ...config.variables // but config level variables take precedence
    },
    cardTemplates: sanitisedCardTemplates,
    steps: sanitisedSteps,
    triggers: sanitisedTriggers
  }
}
